import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { motion } from 'framer-motion';

const BearBoxBreathing = () => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes default
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(4);
  const [selectedDuration, setSelectedDuration] = useState(300);
  const [hasStarted, setHasStarted] = useState(false);
  const intervalRef = useRef(null);
  const breathIntervalRef = useRef(null);

  const durations = [
    { value: 120, label: '2 min' },
    { value: 300, label: '5 min' },
    { value: 600, label: '10 min' }
  ];

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (breathIntervalRef.current) clearInterval(breathIntervalRef.current);
    };
  }, []);

  const startMeditation = () => {
    setTimeLeft(selectedDuration);
    setIsActive(true);
    setHasStarted(true);
    setBreathPhase('inhale');
    setPhaseTimeLeft(4);

    // Main timer countdown
    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          stopMeditation();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    startBreathCycle();
  };

  const startBreathCycle = (startIndex = 0, startCountdown = 4) => {
    // Box breathing: 4 seconds each phase
    // Inhale (4s) -> Hold (4s) -> Exhale (4s) -> Rest (4s)
    const phases = ['inhale', 'hold', 'exhale', 'rest'];
    let currentPhaseIndex = startIndex;
    let countdown = startCountdown;

    breathIntervalRef.current = setInterval(() => {
      countdown -= 1;
      setPhaseTimeLeft(countdown);

      if (countdown <= 0) {
        countdown = 4;
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        setBreathPhase(phases[currentPhaseIndex]);
        setPhaseTimeLeft(4);
      }
    }, 1000);
  };

  const stopMeditation = () => {
    setIsActive(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (breathIntervalRef.current) clearInterval(breathIntervalRef.current);
  };

  const resetMeditation = () => {
    stopMeditation();
    setHasStarted(false);
    setTimeLeft(selectedDuration);
    setBreathPhase('inhale');
    setPhaseTimeLeft(4);
  };

  const togglePause = () => {
    if (isActive) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (breathIntervalRef.current) clearInterval(breathIntervalRef.current);
      setIsActive(false);
    } else {
      // Resume - guard against resuming after the session has already ended
      if (timeLeft <= 0) return;

      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            stopMeditation();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      // Resume the breath cycle from the phase/countdown that was showing when paused,
      // instead of restarting the box-breathing sequence at 'inhale'.
      const phases = ['inhale', 'hold', 'exhale', 'rest'];
      const resumeIndex = phases.indexOf(breathPhase);
      startBreathCycle(resumeIndex === -1 ? 0 : resumeIndex, phaseTimeLeft);
      setIsActive(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getBreathInstruction = () => {
    switch (breathPhase) {
      case 'inhale': return 'Breathe In';
      case 'hold': return 'Hold';
      case 'exhale': return 'Breathe Out';
      case 'rest': return 'Rest';
      default: return '';
    }
  };

  const getPhaseColor = () => {
    switch (breathPhase) {
      case 'inhale': return 'from-blue-400 to-blue-600';
      case 'hold': return 'from-purple-400 to-purple-600';
      case 'exhale': return 'from-green-400 to-green-600';
      case 'rest': return 'from-amber-400 to-amber-600';
      default: return 'from-[#81B29A] to-[#6A9B84]';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF4E6] to-[#E0F7FA] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-300 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => {
              stopMeditation();
              navigate('/dashboard');
            }}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748]">Bear Box Breathing</h1>
          
          <div className="w-24"></div>
        </header>

        {!hasStarted ? (
          // Start Screen
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-slate-100 text-center">
              <div className="text-8xl mb-6">🐻</div>
              
              <h2 className="text-2xl font-bold text-[#2D3748] mb-4">Box Breathing</h2>
              <p className="text-[#4A5568] mb-2">4-4-4-4 Pattern</p>
              <p className="text-[#4A5568] mb-8">
                Deep, grounding breaths to calm your mind and body. 
                Breathe in for 4 seconds, hold for 4, breathe out for 4, rest for 4.
              </p>

              <div className="mb-8">
                <p className="text-sm text-[#4A5568] mb-3">Select Duration</p>
                <div className="flex gap-3 justify-center">
                  {durations.map(d => (
                    <button
                      key={d.value}
                      onClick={() => {
                        setSelectedDuration(d.value);
                        setTimeLeft(d.value);
                      }}
                      className={`px-6 py-3 rounded-full font-semibold transition-all ${
                        selectedDuration === d.value
                          ? 'bg-[#81B29A] text-white'
                          : 'bg-slate-100 text-[#4A5568] hover:bg-slate-200'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="start-meditation-btn"
                onClick={startMeditation}
                className="bg-gradient-to-r from-[#81B29A] to-[#6A9B84] text-white px-12 py-5 rounded-full font-bold text-xl hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-3 mx-auto"
              >
                <Play className="w-6 h-6" />
                Begin
              </button>

              <div className="mt-8 p-4 bg-amber-50 rounded-xl">
                <p className="text-amber-800 text-sm">
                  💡 <strong>Tip:</strong> Find a comfortable position, close your eyes, 
                  and follow the breathing circle.
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Active Meditation Screen
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-slate-100 text-center">
              <div className="text-6xl mb-4">🐻</div>
              
              <p className="text-[#4A5568] mb-2">4-4-4-4 Box Breathing</p>
              <p className="text-lg text-[#81B29A] font-semibold mb-8">Time Remaining: {formatTime(timeLeft)}</p>
              
              <div className="mb-8">
                <motion.div
                  className={`w-56 h-56 mx-auto rounded-full bg-gradient-to-br ${getPhaseColor()} flex items-center justify-center shadow-2xl`}
                  animate={{
                    scale: breathPhase === 'inhale' ? 1.3 : 
                           breathPhase === 'exhale' ? 0.7 : 1
                  }}
                  transition={{ duration: 4, ease: 'easeInOut' }}
                >
                  <div className="text-white text-center">
                    <p className="text-2xl font-bold mb-2">{getBreathInstruction()}</p>
                    <p className="text-6xl font-bold">{phaseTimeLeft}</p>
                  </div>
                </motion.div>
              </div>

              {/* Phase indicators */}
              <div className="flex justify-center gap-2 mb-8">
                {['inhale', 'hold', 'exhale', 'rest'].map(phase => (
                  <div
                    key={phase}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      breathPhase === phase
                        ? 'bg-[#81B29A] text-white scale-110'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {phase === 'inhale' ? 'In' : 
                     phase === 'hold' ? 'Hold' :
                     phase === 'exhale' ? 'Out' : 'Rest'}
                  </div>
                ))}
              </div>
              
              <div className="flex gap-4 justify-center">
                <button
                  data-testid="toggle-pause-btn"
                  onClick={togglePause}
                  disabled={!isActive && timeLeft <= 0}
                  className="bg-[#81B29A] text-white px-8 py-4 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {isActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  {isActive ? 'Pause' : 'Resume'}
                </button>
                
                <button
                  data-testid="stop-meditation-btn"
                  onClick={resetMeditation}
                  className="bg-white border-2 border-[#81B29A] text-[#81B29A] px-8 py-4 rounded-full font-bold hover:bg-[#81B29A]/10 transition-all duration-300"
                >
                  Stop
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BearBoxBreathing;
